import { merge, of, Subject } from 'rxjs';
import {
  filter, mergeMap, switchMap, takeUntil,
} from 'rxjs/operators';

import { Container, Service } from '../../../../symphony';

import { SlActions } from './sl-actions';
import { SlLEContext } from '../../global-constants';
import { SlActionsController } from './sl-actions.controller';
import { IAction, ActionType, RequestType } from '../../global-interface';

@Service()
export class SlActionsService {
  private actions$ = Container.take(SlLEContext, SlActions).actions$;

  private controller = Container.take(SlLEContext, SlActionsController);

  private cancelableActionsMap = {
    // Network communication
    [ActionType.SolveMaterial]: this.controller.solveMaterialHandler,

    // Business actions
    [ActionType.InitializeLessonPage]:
      this.controller.initializeLessonPageHandler,
    [ActionType.ClearLessonData]: this.controller.clearLessonDataHandler,
    [ActionType.SetLessonData]: this.controller.setLessonDataHandler,
    [ActionType.defineLessonQuitPromptInfo]:
      this.controller.defineLessonQuitPromptInfoHandler,
    [ActionType.OnUnlockQuizAnswerButtonClick]:
      this.controller.onUnlockQuizAnswerButtonClickHandler,
  };

  private parallelActionsMap = {
    // Network communication
    [ActionType.GetCourseSubtree]: this.controller.getCourseSubtreeHandler,
    [ActionType.GetLearningPathSubtree]:
      this.controller.getLearningPathSubtreeHandler,
    [ActionType.GetCertificate]: this.controller.getCertificateHandler,
    [ActionType.SetLessonData]: this.controller.setLessonDataHandler,
    [ActionType.SolveMaterial]: this.controller.solveMaterialHandler,
    [ActionType.EnrollCourse]: this.controller.enrollCourseHandler,
    [ActionType.GetLandingPageData]:
      this.controller.getLandingPageDataHandler,
    [ActionType.RefillFreeHearts]: this.controller.refillFreeHeartsHandler,
    [ActionType.GetHeartConfigs]: this.controller.getHeartConfigsHandler,
    [ActionType.GetLessonCompleteScreens]:
      this.controller.getLessonCompleteScreensHandler,
    [ActionType.GetLeaderboardPredictedState]:
      this.controller.getLeaderboardPredictedStateHandler,
    [ActionType.GetExperiments]: this.controller.getExperimentsHandler,
    [ActionType.GetCourseCategories]:
      this.controller.getCourseCategoriesHandler,
    [ActionType.GetProgrammingLanguages]:
      this.controller.getProgrammingLanguagesHandler,
    [ActionType.GetUserSettings]: this.controller.getUserSettingsHandler,
    [ActionType.UpdateUserSettings]:
      this.controller.updateUserSettingsHandler,
    [ActionType.SetStreakGoal]: this.controller.setStreakGoalHandler,

    // Business actions
    [ActionType.StartLessonSessionDataRecording]:
      this.controller.startLessonSessionDataRecordingHandler,
    [ActionType.RemoveLessonSessionDataRecording]:
      this.controller.removeLessonSessionDataRecodingHandler,
    [ActionType.InitializeApp]: this.controller.initializeAppHandler,
    [ActionType.CleanupCourse]: this.controller.cleanupCourseHandler,
    [ActionType.CleanupPath]: this.controller.cleanupPathHandler,
    [ActionType.NavigateToCourse]: this.controller.navigateToCourseHandler,
    [ActionType.GetCourse]: this.controller.getCourseHandler,
    [ActionType.GetLearningPath]: this.controller.getLearningPathHandler,
    [ActionType.StartOnboarding]: this.controller.startOnboardingHandler,
    [ActionType.OpenLesson]: this.controller.openLessonHandler,
    [ActionType.OpenCodeProject]: this.controller.openCodeProjectHandler,
    [ActionType.OpenCodeRepo]: this.controller.openCodeRepoHandler,
    [ActionType.InitializeLessonPage]:
      this.controller.initializeLessonPageHandler,
    [ActionType.SubmitSolutions]: this.controller.submitSolutionsHandler,
    [ActionType.ToNextPage]: this.controller.toNextPageHandler,
    [ActionType.CheckAnswer]: this.controller.checkAnswerHandler,
    [ActionType.TryAgain]: this.controller.tryAgainHandler,
    [ActionType.ToPreviousPage]: this.controller.toPreviousPageHandler,
    [ActionType.CloseLesson]: this.controller.closeLessonHandler,
    [ActionType.DeleteSessionDataFromStorage]:
      this.controller.deleteSessionDataHandler,
    [ActionType.DeleteSessionFromBackend]:
      this.controller.deleteSessionFromBackend,
    [ActionType.CompleteLesson]: this.controller.completeLessonHandler,
    [ActionType.ByPassLesson]: this.controller.byPassLessonHandler,
    [ActionType.ScrollIntoCurrentMaterialElement]:
      this.controller.scrollIntoCurrentMaterialElementHandler,
    [ActionType.TryAchieveStreak]: this.controller.tryAchieveStreakHandler,
    [ActionType.TrySolveMaterial]: this.controller.trySolveMaterialHandler,
    [ActionType.ClearLessonData]: this.controller.clearLessonDataHandler,
    [ActionType.UpdateStatuses]: this.controller.updateStatusesHandler,
    [ActionType.NavigateToUserLogin]:
      this.controller.navigateToUserLoginHandler,
    [ActionType.NavigateToPro]: this.controller.navigateToProHandler,
    [ActionType.OpenUnlockQuizAnswerModal]:
      this.controller.openUnlockQuizAnswerModalHandler,
    [ActionType.SetHeartsData]: this.controller.setHeartsDataHandler,
    [ActionType.OpenHeartModal]: this.controller.openHeartModalHandler,
    [ActionType.SetCourseCurrentItems]:
      this.controller.setCourseCurrentItemsHandler,
    [ActionType.SetPathCurrentItems]:
      this.controller.setPathCurrentItemsHandler,
    [ActionType.OnLessonNotFoundClick]:
      this.controller.onLessonNotFoundClickHandler,
    [ActionType.OnCourseNotFoundClick]:
      this.controller.onCourseNotFoundClickHandler,
    [ActionType.UpdateDataAfterLogin]:
      this.controller.updateDataAfterLoginHandler,
    [ActionType.NextLCSIteration]:
      this.controller.nextLessonCompleteScreenIterationHandler,
    [ActionType.SetBitLocation]: this.controller.setBitLocationHandler,
    [ActionType.DecreaseBitsCount]:
      this.controller.decreaseBitsCountHandler,
    [ActionType.SolveMaterialCallback]:
      this.controller.solveMaterialCallbackHandler,
    [ActionType.MakeMaterialChanges]:
      this.controller.makeMaterialChangesHandler,

    // Mount events
    [ActionType.OnCoursesMount]: this.controller.onCoursesMountHandler,
    [ActionType.OnCourseContainerMount]:
      this.controller.onCourseContainerMountHandler,
    [ActionType.OnLearningPathContainerMount]:
      this.controller.onLearningPathContainerMountHandler,
    [ActionType.OnCurrentModuleChildrenMount]:
      this.controller.onCurrentModuleChildrenMountHandler,
    [ActionType.OnLessonContainerMount]:
      this.controller.onLessonContainerMountHandler,
    [ActionType.OnLessonPageMount]:
      this.controller.onLessonPageMountHandler,
    [ActionType.OnTheoryMount]: this.controller.onTheoryMountHandler,
    [ActionType.OnTIYWidgetMount]: this.controller.onTIYWidgetMountHandler,
    [ActionType.OnRouteWithCourseContainerMount]:
      this.controller.onRouteWithCourseContainerMountHandler,
    [ActionType.OnRouteWithLearningPathContainerMount]:
      this.controller.onRouteWithLearningPathContainerMountHandler,
    [ActionType.OnLessonPageIndexChange]:
      this.controller.onLessonPageIndexChangeHandler,
    [ActionType.OnTIYMaterialMount]:
      this.controller.onTIYMaterialMountHandler,
    [ActionType.OnCCMaterialMount]:
      this.controller.onCCMaterialMountHandler,
    [ActionType.OnCRMaterialMount]:
      this.controller.onCRMaterialMountHandler,
    [ActionType.OnLandingPageMount]:
      this.controller.onLandingPageMountHandler,
    [ActionType.OnCourseMount]: this.controller.onCourseMountHandler,
    [ActionType.OnLessonMount]: this.controller.onLessonMountHandler,
    [ActionType.OnSlLearnEngineMount]:
      this.controller.onSlLearnEngineMountHandler,
    [ActionType.SetLPComments]: this.controller.setLPCommentsHandler,
    [ActionType.OnStreakCelebrationMount]:
      this.controller.onStreakCelebrationMountHandler,
    [ActionType.OnFirstLessonCompleteCelebrationMount]:
      this.controller.OnFirstLessonCompleteCelebrationMountHandler,
    [ActionType.OnLandingPageIsMounted]:
      this.controller.onLandingPageIsMountedHandler,
    [ActionType.OnFreeHeartScreenMount]:
      this.controller.onFreeHeartScreenMountHandler,
    [ActionType.OnLeaderboardScreenMount]:
      this.controller.onLeaderboardScreenMountHandler,
    [ActionType.OnUnlockQuizAnswerMount]:
      this.controller.onUnlockQuizAnswerMountHandler,
    [ActionType.OnStreakGoalScreenMount]:
      this.controller.onStreakGoalScreenMountHandler,
    [ActionType.OnLessonCompletePaywallInit]:
      this.controller.onLessonCompletePaywallInitHandler,
    // Unmount events
    [ActionType.OnSlLearnEngineUnmount]:
      this.controller.onSlLearnEngineUnmountHandler,
    [ActionType.OnCourseUnmount]: this.controller.onCourseUnmountHandler,
    [ActionType.OnLessonContainerUnmount]:
      this.controller.onLessonContainerUnmountHandler,
    [ActionType.OnLessonPageUnmount]:
      this.controller.onLessonPageUnmountHandler,
    [ActionType.OnLeaderboardScreenUnmount]:
      this.controller.onLeaderboardScreenUnmountHandler,
    [ActionType.OnLessonQuitPromptUnmount]:
      this.controller.onLessonQuitPromptUnmountHandler,

    // Click events
    [ActionType.OnCourseClick]: this.controller.onCourseClickHandler,
    [ActionType.OnLearningPathClick]: this.controller.onPathClickHandler,
    [ActionType.OnModuleClick]: this.controller.onModuleClickHandler,
    [ActionType.OnLessonClick]: this.controller.onLessonClickHandler,
    [ActionType.OnCloseLessonClick]:
      this.controller.onCloseLessonClickHandler,
    [ActionType.OnRunTIYClick]: this.controller.onRunTIYClickHandler,
    [ActionType.OnRunExternalResourceClick]:
      this.controller.onRunExternalResourceClickHandler,
    [ActionType.OnRunExternalResourceCloseClick]:
      this.controller.onRunExternalResourceCloseClickHandler,
    [ActionType.onCRPracticeClick]:
      this.controller.onCRPracticeClickHandler,
    [ActionType.onCCPracticeClick]:
      this.controller.onCCPracticeClickHandler,
    [ActionType.onCPPracticeClick]:
      this.controller.onCPPracticeClickHandler,
    [ActionType.OnLPCommentsClick]:
      this.controller.onLPCommentsClickHandler,
    [ActionType.OnCloseLPCommentsClick]:
      this.controller.onCloseLPCommentsClickHandler,
    [ActionType.OnHeartIconClick]: this.controller.onHeartIconClickHandler,
    [ActionType.OnStartLearningClick]:
      this.controller.onStartLearningClickHandler,
    [ActionType.OnCertificateCTAClick]:
      this.controller.onCertificateCTAClickHandler,
    [ActionType.OnCertificateCourseComplete]:
      this.controller.onCertificateCourseCompleteHandler,
    [ActionType.OnLessonCompletedModalContinueClick]:
      this.controller.onLessonCompletedModalContinueClickHandler,
    [ActionType.OnFirstLessonCelebrationContinueClick]:
      this.controller.onFirstLessonCelebrationContinueClickHandler,
    [ActionType.OnStreakCelebrationScreenContinueClick]:
      this.controller.onStreakCelebrationScreenContinueClickHandler,
    [ActionType.OnCodeSnippetClick]:
      this.controller.onCodeSnippetClickHandler,
    [ActionType.OnImageClick]: this.controller.onImageClickHandler,
    [ActionType.OnCloseNotAllowedFeatureModalClick]:
      this.controller.onCloseNotAllowedFeatureModalClickHandler,
    [ActionType.OnLandingPageCTAClick]:
      this.controller.onLandingPageCTAClickHandler,
    [ActionType.OnFreeHeartRefillCtaClick]:
      this.controller.onFreeHeartRefillCtaClickHandler,
    [ActionType.OngoToLeaderboardClick]:
      this.controller.onGoToLeaderboardClickHandler,
    [ActionType.OnLeaderboardScreenContinueClick]:
      this.controller.onLeaderboardScreenContinueClickHandler,
    [ActionType.OnLessonQuitPromptContinueClick]:
      this.controller.onLessonQuitPromptContinueClickHandler,
    [ActionType.OnLessonQuitPromptLeaveClick]:
      this.controller.onLessonQuitPromptLeaveClickHandler,
    [ActionType.OnLockedCardClick]:
      this.controller.onLockedCardClickHandler,
    [ActionType.OnLockedCardContinueClick]:
      this.controller.onLockedCardContinueClickHandler,
    [ActionType.OnUnlockQuizAnswerCloseClick]:
      this.controller.onUnlockQuizAnswerCloseClickHandler,
    [ActionType.OnStreakGoalScreenButtonClick]:
      this.controller.onStreakGoalScreenButtonClickHandler,
    [ActionType.OnKodieIntroClose]:
      this.controller.onKodieIntroCloseHandler,
    [ActionType.OnKodieInNavigationAnimationEnd]:
      this.controller.onKodieInNavigationAnimationEndHandler,
    [ActionType.OnOnboardingCtaClick]:
      this.controller.onOnboardingCtaClickHandler,
    [ActionType.OnTranslateButtonClick]:
      this.controller.onTranslateButtonClickHandler,
    [ActionType.OnSeeOriginalLanguageButtonClickHandler]:
      this.controller.onSeeOriginalLanguageButtonClickHandler,
    [ActionType.OnSeeTranslatedLanguageButtonClickHandler]:
      this.controller.onSeeTranslatedLanguageButtonClickHandler,
    [ActionType.OnCloseTranslationModalClick]:
      this.controller.onCloseTranslationModalClickHandler,
    [ActionType.OnOpenTranslationModalClick]:
      this.controller.onOpenTranslationModalClickHandler,
    [ActionType.OnTranslationWarningCtaClick]:
      this.controller.onTranslationWarningCtaClickHandler,
    [ActionType.OnUpdateSelectedLocale]:
      this.controller.onUpdateSelectedLocaleHandler,

    // Callback events
    [ActionType.OnUpdateLPCommentsCB]:
      this.controller.onUpdateLPCommentsCBHandler,
    [ActionType.OnLandingSignup]: this.controller.onLandingSignupHandler,
    [ActionType.ExpiredSessionIdError]:
      this.controller.expiredSessionIdErrorHandler,
    [ActionType.OnOnboardingCourseReady]:
      this.controller.onOnboardingCourseReadyHandler,
    [ActionType.OnOnboardingLessonReady]:
      this.controller.onOnboardingLessonReadyHandler,
    [ActionType.OnOnboardingLoopIteration]:
      this.controller.onOnboardingLoopIterationHandler,
    [ActionType.OnOnboardingComplete]:
      this.controller.onOnboardingCompleteHandler,

    // Common actions
    [ActionType.OpenModal]: this.controller.openModalHandler,
    [ActionType.CloseModal]: this.controller.closeModalHandler,

    // Track Actions
    [ActionType.Track]: this.controller.onTrackActionHandler,
    [ActionType.TrackFirstRefillImpression]:
      this.controller.onTrackFirstRefillImpressionHandler,
    [ActionType.TrackFirstRefillClick]:
      this.controller.onTrackFirstRefillClickHandler,
    [ActionType.TrackStreakCelebrationImpression]:
      this.controller.onTrackStreakCelebrationHandler,
  };

  constructor() {
    const unsubscriber: Subject<void> = Container.take(
      SlLEContext,
      'unsubscriber',
    );
    const cancelable = this.actions$.pipe(
      filter((a) => !!a && a.requestType === RequestType.Cancelable),
      switchMap((action: IAction) => {
        const handler = this.cancelableActionsMap[action.type];
        return handler ? handler(action.data) : of(action);
      }),
    );

    const parallel = this.actions$.pipe(
      filter((a) => !!a && a.requestType === RequestType.Parallel),
      mergeMap((action: IAction) => {
        const handler = this.parallelActionsMap[action.type];
        return handler ? handler(action.data) : of(action);
      }),
    );

    merge(cancelable, parallel)
      .pipe(
        filter((a) => !!a),
        takeUntil(unsubscriber),
      )
      .subscribe();
  }
}
